import React, { useState, useRef } from 'react';
import { X, Send, Paperclip, MessageSquare } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

/**
 * AdminStartChatModal — lets an admin start a chat with a specific user directly
 * from the AdminUsers list. Sends the first message via the existing
 * POST /api/messages/admin/send endpoint (which also creates the conversation
 * record and notifies the user). Isolated component — does not modify any
 * existing chat, message, or user-management logic.
 */
const AdminStartChatModal = ({ user, onClose }) => {
  const { toast } = useToast();
  const [messageText, setMessageText] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  if (!user) return null;

  const handleSend = async () => {
    const text = messageText.trim();
    if (!text && !file) {
      toast({
        title: 'Message required',
        description: 'Type a message or attach a file before sending.',
        variant: 'destructive',
      });
      return;
    }

    setSending(true);
    try {
      const token =
        localStorage.getItem('secure_admin_token') ||
        localStorage.getItem('admin_token');

      const formData = new FormData();
      formData.append('user_id', user.user_id || user._id);
      formData.append('message_text', text || '(File attachment)');
      if (file) formData.append('attachment', file);

      const response = await fetch('/api/messages/admin/send', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to send message');
      }

      toast({
        title: 'Chat started',
        description: `Message sent to ${user.username || user.email}. Continue the conversation in Support Messages.`,
      });
      onClose(true);
    } catch (error) {
      toast({
        title: 'Send failed',
        description: error.message || 'Could not send message. Try again.',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="admin-start-chat-modal"
      onClick={() => !sending && onClose(false)}
    >
      <div
        className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-cyan-500 rounded-xl p-6 max-w-lg w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-cyan-500/20 border border-cyan-500 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white" data-testid="chat-modal-title">
                Start Chat with {user.username || 'User'}
              </h3>
              <p className="text-xs text-cyan-400">{user.email}</p>
            </div>
          </div>
          <button
            onClick={() => !sending && onClose(false)}
            className="w-8 h-8 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center text-white transition-all"
            data-testid="chat-modal-close-btn"
            disabled={sending}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Your Message
            </label>
            <textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              rows={5}
              placeholder="Hi, how can we help you today?"
              className="w-full px-4 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400 resize-none"
              data-testid="chat-modal-message-input"
              disabled={sending}
              autoFocus
            />
          </div>

          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Attachment <span className="text-slate-500 font-normal">(optional)</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="chat-modal-file-input"
                disabled={sending}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 border border-cyan-500/30 rounded-lg text-white text-sm transition-all"
                disabled={sending}
                data-testid="chat-modal-attach-btn"
              >
                <Paperclip className="w-4 h-4" />
                {file ? 'Change file' : 'Choose file'}
              </button>
              {file && (
                <span className="text-slate-300 text-sm truncate max-w-[220px]">
                  {file.name}
                </span>
              )}
              {file && (
                <button
                  type="button"
                  onClick={() => {
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="text-red-400 hover:text-red-300"
                  data-testid="chat-modal-clear-file-btn"
                  disabled={sending}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={() => !sending && onClose(false)}
            className="flex-1 px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white font-semibold transition-all disabled:opacity-50"
            disabled={sending}
            data-testid="chat-modal-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="flex-1 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="chat-modal-send-btn"
          >
            <Send className="w-4 h-4" />
            {sending ? 'Sending...' : 'Send Message'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminStartChatModal;
